import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { RouteComponentProps, Link as RouterLink } from 'react-router-dom';
import { getIDFromUrl } from '@rossum/api-client';
import { shouldDisplayAuroraChipByName } from '@rossum/components/aurora-chip/AuroraChip';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import { IconBrain, IconCustomAurora } from '@rossum/ui/icons/tabler';
import { Link, Stack, SvgIcon, Tooltip, Typography } from '@rossum/ui/material';
import { useGetInbox } from '../../../../features/document-upload/hooks/useGetInbox';
import { useEngine } from '../../../../features/engines/hooks/useEngine';
import { engineBasicSettingsPath } from '../../../../features/engines/routes';
import { useDedicatedEngine } from '../../../../features/legacy-engines/hooks/useDedicatedEngine';
import { useGenericEngine } from '../../../../features/legacy-engines/hooks/useGenericEngine';
import { BasicSettingsHeader } from '../../../../features/queue-settings/headers/BasicSettingsHeader';
import { useQueueWithWorkspace } from '../../../../features/queues/hooks/useQueueWithWorkspace';
import { auroraEngineEnabledSelector } from '../../../../redux/modules/organizationGroup/selectors';
import { automationEnginesPath } from '../../../Automation/helpers';
import { StatCard } from '../../../Settings/components/StatCard';
import { QueueStats } from '../../../Settings/containers/QueueStats';
import { QueueBasicSettingsContainer } from './components/QueueBasicSettingsContainer';

type QueueBasicSettingsProps = RouteComponentProps<{ queueId: string }>;

const truncationLimit = 30;

export const QueueBasicSettings = ({
  match: {
    url,
    params: { queueId },
  },
}: QueueBasicSettingsProps) => {
  const intl = useIntl();

  const { queue, workspace } = useQueueWithWorkspace({
    queueId: Number(queueId),
  });

  const { data: inbox } = useGetInbox(
    queue && queue.inbox ? getIDFromUrl(queue?.inbox) : 0
  );

  // <queue-is-only-allowed-to-have-one-engine-so-we-condition-these-queries>

  const genericEngineId = queue?.genericEngine
    ? getIDFromUrl(queue.genericEngine)
    : null;
  const { data: genericEngine } = useGenericEngine(genericEngineId);

  const dedicatedEngineId =
    !genericEngineId && queue?.dedicatedEngine
      ? getIDFromUrl(queue.dedicatedEngine)
      : null;

  const { data: dedicatedEngine } = useDedicatedEngine(dedicatedEngineId);

  const auroraEngineEnabled = useSelector(auroraEngineEnabledSelector);

  const engineId =
    !dedicatedEngineId && queue?.engine ? getIDFromUrl(queue.engine) : null;

  const { data: engineData } = useEngine(engineId);

  // </queue-is-only-allowed-to-have-one-engine-so-we-condition-these-queries>

  if (!queue || !workspace) return null;

  const { engine, enginePath } = {
    engine: dedicatedEngine ?? genericEngine ?? engineData,
    enginePath: engineData
      ? engineBasicSettingsPath(engineData.id)
      : automationEnginesPath(),
  };

  const description = engine ? engine.description : '-';

  const truncatedDescription = R.truncate(description ?? '', truncationLimit, {
    omission: '… ',
  });

  const engineName = engine ? engine.name : '-';

  const shouldDisplayAuroraChip = shouldDisplayAuroraChipByName(
    engineName,
    auroraEngineEnabled
  );

  return (
    <PageLayout
      renderHeader={params => (
        <BasicSettingsHeader
          scrollableDivRef={params.scrollableDivRef}
          queue={queue}
          workspaceName={workspace.name}
          parentPath={url}
        />
      )}
    >
      <Stack
        direction="row"
        spacing={2}
        useFlexGap
        sx={{ my: 4, flexWrap: 'wrap' }}
      >
        <Stack sx={{ flexGrow: 1, flexBasis: '30%' }}>
          <StatCard
            icon={
              shouldDisplayAuroraChip ? (
                <SvgIcon fontSize="large" color="primary">
                  <IconCustomAurora />
                </SvgIcon>
              ) : (
                <SvgIcon fontSize="large" color="primary">
                  <IconBrain />
                </SvgIcon>
              )
            }
            title={engineName}
            titleDataCy="stats-value-engine"
            description={
              <Typography variant="caption" color="text.disabled">
                <Tooltip
                  placement="top"
                  enterDelay={500}
                  title={
                    truncatedDescription !== description ? description : ''
                  }
                  arrow
                >
                  <span>{truncatedDescription}</span>
                </Tooltip>
                <Link
                  component={RouterLink}
                  to={enginePath}
                  sx={{
                    ml: 0.5,
                    color: 'inherit',
                    textDecorationColor: 'inherit',
                    '&:hover': {
                      color: 'inherit',
                      textDecoration: 'none',
                    },
                  }}
                >
                  {intl.formatMessage({
                    id: 'containers.settings.queues.engine.linkMore',
                  })}
                </Link>
              </Typography>
            }
          />
        </Stack>
        {queue ? <QueueStats selectedQueue={queue} /> : null}
      </Stack>
      <QueueBasicSettingsContainer selectedQueue={queue} inbox={inbox} />
    </PageLayout>
  );
};
